import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FeatherRefreshCw } from "@subframe/core";
import {
  fetchGlManagementTree,
  updateGlBillInvoiceFlags,
} from "../../data/api/adminGlMappingsApi";
import {
  mapDtoToGlTreeNode,
  countGlTreeLeaves,
  type GlTreeNode,
} from "../../data/mappers/GlManagementTreeMapper";
import { Button } from "../../../../ui/components/Button";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import {
  GlManagementTreeRows,
  GlManagementTreeTableHeader,
} from "./GlManagementTreeRows";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

interface GlFlags {
  bill: boolean;
  invoice: boolean;
}

function buildFlagsFromTree(nodes: GlTreeNode[]): Record<number, GlFlags> {
  const flags: Record<number, GlFlags> = {};

  const walk = (list: GlTreeNode[]) => {
    for (const node of list) {
      if (!node.hasChildren && node.id > 0) {
        flags[node.id] = { bill: node.bill, invoice: node.invoice };
      }
      if (node.children.length > 0) {
        walk(node.children);
      }
    }
  };

  walk(nodes);
  return flags;
}

interface GlFlagCheckboxProps {
  glId: number;
  field: "bill" | "invoice";
  checked: boolean;
  disabled: boolean;
  onChange: (glId: number, field: "bill" | "invoice", checked: boolean) => void;
}

function GlFlagCheckbox({
  glId,
  field,
  checked,
  disabled,
  onChange,
}: GlFlagCheckboxProps) {
  return (
    <Checkbox
      label=""
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value) => onChange(glId, field, Boolean(value))}
    />
  );
}

export function AdminGlMappingsTable() {
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<GlTreeNode[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flags, setFlags] = useState<Record<number, GlFlags>>({});
  const [dirtyIds, setDirtyIds] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const fetchRequestIdRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchDebounced(search.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadTree = useCallback(async (pageIndex: number, searchQuery: string) => {
    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchGlManagementTree({
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
        search: searchQuery || undefined,
      });
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      const nodes = (response.data.items ?? []).map(mapDtoToGlTreeNode);
      setItems(nodes);
      setTotalItems(response.data.pagination?.total_items ?? 0);
      setHasMore(Boolean(response.data.pagination?.has_more));
      setFlags(buildFlagsFromTree(nodes));
      setDirtyIds(new Set());
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      const message =
        err instanceof Error ? err.message : "Failed to load GL mappings";
      setError(message);
      setItems([]);
      setTotalItems(0);
      setHasMore(false);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadTree(page, searchDebounced);
  }, [loadTree, page, searchDebounced]);

  const handleFlagChange = useCallback(
    (glId: number, field: "bill" | "invoice", checked: boolean) => {
      if (glId <= 0) return;
      setFlags((prev) => ({
        ...prev,
        [glId]: {
          bill: field === "bill" ? checked : (prev[glId]?.bill ?? false),
          invoice:
            field === "invoice" ? checked : (prev[glId]?.invoice ?? false),
        },
      }));
      setDirtyIds((prev) => new Set(prev).add(glId));
    },
    [],
  );

  const handleRefresh = () => {
    setSearch("");
    setSearchDebounced("");
    setPage(0);
    void loadTree(0, "");
  };

  const handleSaveChanges = async () => {
    if (dirtyIds.size === 0) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        Array.from(dirtyIds).map((glId) => {
          const nodeFlags = flags[glId];
          if (!nodeFlags) return Promise.resolve();
          return updateGlBillInvoiceFlags(glId, {
            bill: nodeFlags.bill,
            invoice: nodeFlags.invoice,
          });
        }),
      );
      await loadTree(page, searchDebounced);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update GL mappings";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const renderBill = useCallback(
    (node: GlTreeNode) => {
      const isUnderReview = node.status === "under_review";
      const nodeFlags = flags[node.id];
      if (node.hasChildren) {
        return null;
      }
      return (
        <GlFlagCheckbox
          glId={node.id}
          field="bill"
          checked={nodeFlags?.bill ?? node.bill}
          disabled={saving || isUnderReview || node.id <= 0}
          onChange={handleFlagChange}
        />
      );
    },
    [flags, saving, handleFlagChange],
  );

  const renderInvoice = useCallback(
    (node: GlTreeNode) => {
      const isUnderReview = node.status === "under_review";
      const nodeFlags = flags[node.id];
      if (node.hasChildren) {
        return null;
      }
      return (
        <GlFlagCheckbox
          glId={node.id}
          field="invoice"
          checked={nodeFlags?.invoice ?? node.invoice}
          disabled={saving || isUnderReview || node.id <= 0}
          onChange={handleFlagChange}
        />
      );
    },
    [flags, saving, handleFlagChange],
  );

  const visibleLeafCount = useMemo(() => countGlTreeLeaves(items), [items]);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const showingFrom = totalItems === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, totalItems);

  return (
    <>
      {dirtyIds.size > 0 && (
        <div className="sticky top-0 z-10 flex w-full items-center justify-between gap-4 rounded-lg border border-solid border-brand-200 bg-brand-50 px-4 py-3 shadow-sm">
          <span className="text-body font-body text-brand-800">
            You have unsaved changes to GL bill/invoice flags.
          </span>
          <Button
            variant="brand-primary"
            onClick={() => void handleSaveChanges()}
            disabled={saving || loading}
          >
            {saving ? "Updating..." : "Update"}
          </Button>
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
        <div className="flex w-full items-start justify-between gap-4 mobile:flex-col">
          <div className="flex flex-col items-start gap-1">
            <span className="text-body-bold font-body-bold text-default-font">
              GL Mappings
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Only leaf accounts can be flagged for Bill/Invoice. Parent accounts
              are shown for context but cannot be ticked.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <TextField
              className="h-auto w-72 flex-none mobile:grow mobile:shrink-0 mobile:basis-0"
              variant="outline"
              label=""
              helpText=""
            >
              <TextField.Input
                placeholder="Search by GL name or account type"
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearch(event.target.value)
                }
              />
            </TextField>
            <Button
              variant="neutral-secondary"
              icon={<FeatherRefreshCw />}
              onClick={handleRefresh}
              disabled={saving || loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </Button>
          </div>
        </div>

        {error && (
          <div className="flex w-full rounded-lg border border-solid border-red-200 bg-red-50 p-4">
            <span className="text-body font-body text-red-600">{error}</span>
          </div>
        )}

        <div className="flex w-full flex-col items-start overflow-x-auto">
          <Table header={<GlManagementTreeTableHeader />}>
            {loading && items.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={5}>
                  <span className="text-body font-body text-subtext-color py-4 block">
                    Loading...
                  </span>
                </Table.Cell>
              </Table.Row>
            ) : items.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={5}>
                  <span className="text-body font-body text-subtext-color py-4 block">
                    {searchDebounced
                      ? "No accounts match your search."
                      : "No GL mappings found."}
                  </span>
                </Table.Cell>
              </Table.Row>
            ) : (
              <GlManagementTreeRows
                nodes={items}
                renderBill={renderBill}
                renderInvoice={renderInvoice}
              />
            )}
          </Table>
        </div>

        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="text-caption font-caption text-subtext-color">
            {totalItems > 0
              ? `Showing ${showingFrom}–${showingTo} of ${totalItems} account group${totalItems === 1 ? "" : "s"} (${visibleLeafCount} visible leaf${visibleLeafCount === 1 ? "" : "s"} on this page)`
              : "No account groups to display"}
          </span>
          {totalItems > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="neutral-secondary"
                size="small"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={loading || saving || page === 0}
              >
                Previous
              </Button>
              <span className="text-caption font-caption text-subtext-color">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="neutral-secondary"
                size="small"
                onClick={() => setPage((p) => p + 1)}
                disabled={loading || saving || !hasMore}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AdminGlMappingsTable;
